/**
 * A subscription's real usage, as the provider reports it.
 *
 * Lifted out of the accounts screen so the cockpit can show the same quota bars
 * for the account a live session is running on. It is the account's actual
 * capacity — one bar per rate window, percent USED, the provider's own number —
 * not a derived remaining figure, and never a fabricated one: when the provider
 * publishes nothing this renders nothing.
 */
import { Meter } from './primitives'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import { usageSeverityTone } from '@/lib/tone'
import './AccountUsage.css'

/** api.UsageWindowResponse — one rate-limit window's utilisation (percent USED,
 *  0..100) as the provider reported it. */
export interface UsageWindow {
  kind: string
  group: string
  utilization: number
  severity?: string
  resetAt?: string
  isActive: boolean
  scopeModel?: string
  /** How long this window is, where the provider says so. Codex publishes it per
   *  window; Claude publishes only the next reset. Absent is *unmeasured*, and a
   *  reader that needs a length must say it does not know rather than pick one. */
  periodMinutes?: number
}

/** api.AccountUsageResponse — utilisation across a subscription's windows, with
 *  the provider's own fetch time so freshness is legible. */
export interface AccountUsage {
  fetchedAt: string
  windows?: UsageWindow[]
  spendUsedMinor: number
  spendCurrency?: string
  spendEnabled: boolean
  extraEnabled: boolean
  source?: string
}

function formatSpend(minor: number, currency?: string): string {
  const major = (minor / 100).toFixed(2)
  return currency ? `${major} ${currency}` : major
}

export function AccountUsagePanel({ usage, label }: { usage: AccountUsage; label: string }) {
  const t = useT()
  const { locale } = useLocale()
  // Active windows first — those are the ones currently binding the next turn.
  const windows = [...(usage.windows ?? [])].sort((a, b) => Number(b.isActive) - Number(a.isActive))
  return (
    <div className="k-usage">
      <div className="k-usage__topline">
        <span className="k-label">{t('account.usage')}</span>
        <span className="k-usage__meta">
          {t('common.source')}:{' '}
          <span className="k-mono">
            {(usage.source ?? '').replace('provider-config:', '') || '—'}
          </span>{' '}
          · {formatRelative(usage.fetchedAt, locale)}
        </span>
      </div>
      {windows.map((w) => (
        <div key={w.kind} className="k-usage__row">
          <div className="k-usage__head">
            <span className="k-usage__name">
              {w.kind === 'session'
                ? t('account.usageWindow.session')
                : w.kind === 'weekly_all'
                  ? t('account.usageWindow.weekly')
                  : w.kind === 'monthly_all'
                    ? t('account.usageWindow.monthly')
                    : w.kind === 'weekly_scoped'
                      ? t('account.usageWindow.weeklyScoped')
                      : w.kind}
              {w.scopeModel && <span className="k-mono k-usage__scope"> · {w.scopeModel}</span>}
            </span>
            <span className="k-mono k-usage__pct">{Math.round(w.utilization)}%</span>
          </div>
          <Meter
            value={Math.min(1, w.utilization / 100)}
            tone={usageSeverityTone(w.severity, w.utilization)}
            ariaLabel={`${label} ${w.kind}`}
          />
          {w.resetAt && (
            <span className="k-usage__reset">
              {t('account.usageResets', { when: formatRelative(w.resetAt, locale) })}
            </span>
          )}
        </div>
      ))}
      {usage.spendEnabled ? (
        <p className="k-usage__spend">
          {t('account.spend')}:{' '}
          <span className="k-mono">{formatSpend(usage.spendUsedMinor, usage.spendCurrency)}</span>
        </p>
      ) : (
        <p className="k-usage__spend k-usage__spend--off">{t('account.extraUsageOff')}</p>
      )}
    </div>
  )
}
